import { useEffect, useMemo, useRef, useState } from "react";
import {
  YOUTUBE_NOCOOKIE_ORIGIN,
  isYoutubePlayerReadyEvent,
  postYoutubeCommand,
  postYoutubeListening,
  readYoutubeMessage,
} from "@/shared/youtube/youtubePlayer";
import { useLocale } from "@/shared/i18n/LocaleContext";
import styles from "./TestimonialVideo.module.css";

type PlayerCommand = "playVideo" | "pauseVideo" | "mute" | "unMute";

/**
 * Mesmo player dos depoimentos do educacao: formato Shorts, dá play sozinho
 * quando o quadro está inteiro na viewport e pausa ao sair, via postMessage da
 * Iframe API. Começa mutado (autoplay com som é bloqueado por todo navegador) e
 * o som destrava na primeira interação real do usuário em qualquer lugar da
 * página — scroll não conta como gesto. Com prefers-reduced-motion o play é
 * manual.
 *
 * Comandos só saem depois do `onReady`. Mandar play/pause enquanto o iframe
 * ainda é about:blank (origem do site) gera o aviso de postMessage e pode
 * derrubar o widget do YouTube (`isExternalMethodAvailable`).
 */
export function TestimonialVideo({
  videoId,
  title,
  className,
}: Readonly<{ videoId: string; title: string; className?: string }>) {
  const isEn = useLocale().locale === "en";
  const frameRef = useRef<HTMLIFrameElement>(null);
  const playerReadyRef = useRef(false);
  const desiredCommandRef = useRef<PlayerCommand | null>(null);
  const [isMuted, setIsMuted] = useState(true);
  const isMutedRef = useRef(isMuted);
  isMutedRef.current = isMuted;
  const [pageOrigin, setPageOrigin] = useState("");

  const sendCommand = (command: PlayerCommand) => {
    if (!playerReadyRef.current) return;
    postYoutubeCommand(frameRef.current, command);
  };

  const embedSrc = useMemo(() => {
    const params = new URLSearchParams({
      enablejsapi: "1",
      mute: "1",
      playsinline: "1",
      controls: "0",
      rel: "0",
      loop: "1",
      playlist: videoId,
    });
    if (pageOrigin) params.set("origin", pageOrigin);
    return `${YOUTUBE_NOCOOKIE_ORIGIN}/embed/${videoId}?${params.toString()}`;
  }, [pageOrigin, videoId]);

  useEffect(() => {
    setPageOrigin(window.location.origin);
  }, []);

  useEffect(() => {
    const frame = frameRef.current;
    const wrapper = frame?.parentElement;
    if (!frame || !wrapper) return undefined;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;

    playerReadyRef.current = false;

    const flush = () => {
      const command = desiredCommandRef.current;
      if (!playerReadyRef.current || command === null) return;
      postYoutubeCommand(frame, command);
    };

    const pingListening = () => {
      postYoutubeListening(frame, `testimonial-${videoId}`);
    };

    const onMessage = (event: MessageEvent) => {
      const payload = readYoutubeMessage(event, frame);
      if (!payload) return;
      if (!isYoutubePlayerReadyEvent(payload.event) || playerReadyRef.current) return;
      playerReadyRef.current = true;
      postYoutubeCommand(frame, isMutedRef.current ? "mute" : "unMute");
      flush();
    };

    window.addEventListener("message", onMessage);
    frame.addEventListener("load", pingListening);
    pingListening();
    const retry = window.setInterval(() => {
      if (playerReadyRef.current) window.clearInterval(retry);
      else pingListening();
    }, 250);
    const giveUp = window.setTimeout(() => window.clearInterval(retry), 15000);

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const fullyVisible = entry.isIntersecting && entry.intersectionRatio >= 0.99;
          desiredCommandRef.current = fullyVisible ? "playVideo" : "pauseVideo";
          flush();
        }
      },
      { threshold: [0, 0.5, 0.99, 1] },
    );
    observer.observe(wrapper);

    return () => {
      playerReadyRef.current = false;
      frame.removeEventListener("load", pingListening);
      window.removeEventListener("message", onMessage);
      window.clearInterval(retry);
      window.clearTimeout(giveUp);
      observer.disconnect();
    };
  }, [embedSrc, videoId]);

  useEffect(() => {
    if (!isMuted) return undefined;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;

    const unlockSound = () => {
      sendCommand("unMute");
      setIsMuted(false);
    };

    window.addEventListener("pointerdown", unlockSound, { once: true, passive: true });
    window.addEventListener("keydown", unlockSound, { once: true });

    return () => {
      window.removeEventListener("pointerdown", unlockSound);
      window.removeEventListener("keydown", unlockSound);
    };
  }, [isMuted]);

  return (
    <div className={[styles.frame, className].filter(Boolean).join(" ")}>
      <iframe
        ref={frameRef}
        className={styles.player}
        src={embedSrc}
        title={title}
        loading="lazy" /* fica lá embaixo: não disputa banda com o vídeo do hero */
        allow="autoplay; encrypted-media; picture-in-picture"
        allowFullScreen
      />
      <button
        type="button"
        className={styles.muteToggle}
        aria-label={isMuted ? (isEn ? "Unmute video" : "Ativar som do vídeo") : isEn ? "Mute video" : "Silenciar vídeo"}
        onClick={() => {
          sendCommand(isMuted ? "unMute" : "mute");
          setIsMuted((current) => !current);
        }}
      >
        <svg viewBox="0 0 24 24" width={18} height={18} aria-hidden="true">
          <path d="M3 9v6h4l5 5V4L7 9H3z" />
          {isMuted ? (
            <path d="M16.5 8.5 15 10l2 2-2 2 1.5 1.5 2-2 2 2L22 14l-2-2 2-2-1.5-1.5-2 2-2-2z" />
          ) : (
            <path d="M14.5 8.5a4.5 4.5 0 0 1 0 7v-1.7a2.9 2.9 0 0 0 0-3.6V8.5zm0-3.6a8 8 0 0 1 0 14.2v-1.7a6.3 6.3 0 0 0 0-10.8V4.9z" />
          )}
        </svg>
      </button>
    </div>
  );
}
